import React, { useEffect, useState, useCallback } from "react";
import BaseLayout from "./BaseLayout";
import FlameLoader from './FlameLoader';
import "../App.css";
import { API_BASE } from '../api';
import { fetchAllPages } from '../apiUtils';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [friendIds, setFriendIds] = useState(null); // null = not loaded yet
  const navigate = useNavigate();
  const { authData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("rank");
  const [friendsOnly, setFriendsOnly] = useState(false);
  const minimumGamesThreshold = 40; 
  const winBonusMultiplier = 0.3;
  const lossBonusMultiplier = 0.2;
  const gameMultiplier = 0.1;

  const calculatePercentage = (wins, losses) => {
    if (wins === 0 && losses === 0) {
      const value = 0;
      return value.toFixed(1);
    }
    return ((wins / (wins + losses)) * 100).toFixed(1);
  };

  const handleSortOptionChange = (option) => {
    setSortOption(option);
  };

  const sortUsers = useCallback((users) => {
    return users.sort((a, b) => {
      const totalGamesA = a.score + a.losses;
      const totalGamesB = b.score + b.losses;
      let winPercentageA = calculatePercentage(a.score, a.losses);
      if (winPercentageA === 100.0) {
        winPercentageA = 200.0;
      }
      let winPercentageB = calculatePercentage(b.score, b.losses);
      if (winPercentageB === 100.0) {
        winPercentageB = 200.0;
      }
  
      const gameModifierA = Math.min(totalGamesA / minimumGamesThreshold, 1);
      const gameModifierB = Math.min(totalGamesB / minimumGamesThreshold, 1);
  
      const adjustedScoreA = (winPercentageA * gameModifierA) + (a.score * winBonusMultiplier) - (a.losses * lossBonusMultiplier) + ((totalGamesA / 5) * gameMultiplier);
      const adjustedScoreB = (winPercentageB * gameModifierB) + (b.score * winBonusMultiplier) - (b.losses * lossBonusMultiplier) + ((totalGamesB / 5) * gameMultiplier);
  
      return adjustedScoreB - adjustedScoreA || totalGamesB - totalGamesA;
    });
  }, [minimumGamesThreshold, winBonusMultiplier, lossBonusMultiplier, gameMultiplier]);

  const sortLeaderboardData = (data, option) => {
    switch (option) {
      case "rank":
        // Sort by the backend-assigned rank (already dense-ranked correctly).
        // For same-rank ties, secondary sort by total games descending.
        return data.sort((a, b) => {
          const rankA = a.rank ?? Infinity;
          const rankB = b.rank ?? Infinity;
          if (rankA !== rankB) return rankA - rankB;
          return (b.score + b.losses) - (a.score + a.losses);
        });
      case "wins":
        return data.sort((a, b) => b.score - a.score);
      case "games":
        return data.sort((a, b) => (b.losses + b.score) - (a.losses + a.score));
      case "percentage":
        return data.sort((a, b) => calculatePercentage(b.score, b.losses) - calculatePercentage(a.score, a.losses));
      default:
        return data;
    }
  };

  useEffect(() => {
    document.body.classList.add("account-page-bg");
    const fetchLeaderboardData = async () => {
      try {
        const users = await fetchAllPages(`${API_BASE}/data/get-users`, 'users');
        const sortedData = sortUsers(users);
        setLeaderboardData(sortedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setLoading(false);
      }
    };

    fetchLeaderboardData();
    return () => {
      document.body.classList.remove("account-page-bg");
    };
  }, [sortUsers]);

  // Fetch friend IDs once when authenticated
  useEffect(() => {
    if (!authData?.token) return;
    axios.get(`${API_BASE}/user/friend-ids`, {
      headers: { Authorization: `Bearer ${authData.token}` },
    })
      .then(res => setFriendIds(res.data.friend_ids || []))
      .catch(() => setFriendIds([]));
  }, [authData?.token]);

  const currentUserId = authData?.user?.id;

  // Determine if the current user is unranked
  const currentUserEntry = leaderboardData.find(u => u.id === currentUserId);
  const currentUserUnranked = authData?.isAuthenticated && currentUserEntry && !currentUserEntry.rank;

  // Only show users who have been assigned a rank (excludes brand-new accounts)
  const rankedData = leaderboardData.filter(u => u.rank != null && u.rank > 0);

  // Apply friends filter: include self + friends (still only ranked users)
  const friendsFilteredData = friendsOnly && friendIds !== null
    ? rankedData.filter(u => u.id === currentUserId || friendIds.includes(u.id))
    : rankedData;

  // Detect whether the user simply has no friends (ranked or not)
  const hasFriends = friendIds !== null && friendIds.length > 0;
  const visibleData = friendsFilteredData;

  const sortedLeaderboardData = sortLeaderboardData([...visibleData], sortOption);

  // Assign dense display ranks within the visible sorted list.
  // Works uniformly for every sort option: ties share the same display rank.
  const getTieKey = (user) => {
    switch (sortOption) {
      case 'rank':       return user.rank ?? Infinity;
      case 'wins':       return user.score;
      case 'games':      return user.score + user.losses;
      case 'percentage': return calculatePercentage(user.score, user.losses); // string "50.0" — safe for ===
      default:           return null;
    }
  };

  // Pass 1: competition ranking (1, 2, 2, 4) — when the tie-key changes, the
  // new rank jumps to the current 1-based position, skipping over the tie group.
  let currentRank = 1;
  const ranked = sortedLeaderboardData.map((user, idx, arr) => {
    if (idx > 0 && getTieKey(user) !== getTieKey(arr[idx - 1])) {
      currentRank = idx + 1; // skip positions consumed by the preceding tie group
    }
    return { ...user, displayRank: currentRank };
  });

  // Pass 2: mark entries that share a displayRank with at least one other entry
  const withDisplayRank = ranked.map(user => ({
    ...user,
    tied: ranked.filter(u => u.displayRank === user.displayRank).length > 1,
  }));

  const handleUserClick = (username) => {
    navigate(`/user/${username}`);
  };

  return (
    <BaseLayout>
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">Leaderboard</h1>
          <div className="leaderboard-sort-options">
            {authData?.isAuthenticated && (
              <div className="lb-scope-toggle">
                <button
                  className={`lb-scope-btn${!friendsOnly ? ' active' : ''}`}
                  onClick={() => setFriendsOnly(false)}
                >
                  Global
                </button>
                <button
                  className={`lb-scope-btn${friendsOnly ? ' active' : ''}`}
                  onClick={() => setFriendsOnly(true)}
                >
                  Friends
                </button>
              </div>
            )}
            <label htmlFor="sortOption">Sort by:</label>
            <select
              id="sortOption"
              value={sortOption}
              onChange={(e) => handleSortOptionChange(e.target.value)}
            >
              <option value="rank">Rank</option>
              <option value="wins">Num Wins</option>
              <option value="games">Total Games</option>
              <option value="percentage">Win %</option>
            </select>
          </div>
        </div>
        {currentUserUnranked && (
          <div className="lb-unranked-notice">
            <span>You haven't been ranked yet. Rankings update every few hours. Make some picks in the meantime!</span>
          </div>
        )}
        <div className="leaderboard-content">
          {loading ? (
            <FlameLoader text="Loading leaderboard..." />
          ) : (
            <div className="mp-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>User</th>
                  <th>Overall</th>
                  <th>Win %</th>
                </tr>
              </thead>
              <tbody>
                {withDisplayRank.length === 0 && friendsOnly ? (
                  <tr>
                    <td colSpan="4" className="lb-no-friends-cell">
                      <div className="lb-no-friends-msg">
                        <span>{hasFriends ? "None of your friends have been ranked yet." : "You haven't added any friends yet."}</span>
                        {!hasFriends && (
                          <button className="lb-add-friends-btn" onClick={() => navigate('/friends')}>Find players &rarr;</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  withDisplayRank.map((user) => {
                    const medalCls = user.displayRank === 1 ? ' lb-rank--gold'
                                   : user.displayRank === 2 ? ' lb-rank--silver'
                                   : user.displayRank === 3 ? ' lb-rank--bronze'
                                   : '';
                    return (
                    <tr key={user.id} className={user.id === currentUserId ? 'lb-self-row' : ''}>
                      <td className={(user.tied ? 'lb-tied-rank' : '') + medalCls}>
                        {user.tied ? '=' : ''}#{user.displayRank}
                      </td>
                      <td onClick={() => handleUserClick(user.username)} className="leaderboard-rank-link">
                        <span className="leaderboard-user-cell">
                          <img
                            src={user.profile_picture || '/default.jpg'}
                            alt={user.username}
                            className="leaderboard-avatar"
                            onError={e => { e.target.src = '/default.jpg'; }}
                          />
                          {`${user.firstName} ${user.lastName}`}
                        </span>
                      </td>
                      <td>{user.score} - {user.losses}</td>
                      <td>{calculatePercentage(user.score, user.losses)}%</td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default Leaderboard;
